/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { getUrlSync } from '@msdyn365-commerce/core';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';

import { PastTransaction } from '../../actions/ecomm-interfaces';
import { getCustomerOrderHistory } from '../../actions/helper-functions';

import { IEcommHistoryFilterData } from './ecomm-history-filter.data';
import { IEcommHistoryFilterProps } from './ecomm-history-filter.props.autogenerated';

export interface IEcommHistoryFilterViewProps extends IEcommHistoryFilterProps<IEcommHistoryFilterData> {
    filterNodes: any[];
    extendedNodes: string[];
    totalProducts: number;
}

export interface IFilterTreeView {
  node?: ITreeViewNode[];
}

export interface ITreeViewNode {
  value: string;
  label: string;
  name: string;
  children?: ITreeViewNode[];
}

export interface IEcommHistoryFilterState {
  filterTreeNodesState: ITreeViewNode[];
  extendedNodes: string[];
  totalProducts: number;
  transactions: PastTransaction[];
}

/**
 *
 * EcommHistoryFilter component
 * @extends {React.PureComponent<IEcommHistoryFilterProps<IEcommHistoryFilterData>>}
 */
class EcommHistoryFilter extends React.PureComponent<IEcommHistoryFilterProps<IEcommHistoryFilterData>> {
  public state: IEcommHistoryFilterState = { filterTreeNodesState: [], extendedNodes: [], totalProducts: 0, transactions: [] };

  private extendedNodes: string[] = [];

  public async componentDidMount() {
    const { context, data: { hierarchy } } = this.props;

    const customer = this.props.data.accountInformation?.result;

    if (customer) {
      const orderHistory = await getCustomerOrderHistory(context, hierarchy, customer, this.props.config.organisation ? this.props.config.organisation : 'BT', this.props.config.isUat, this.props.config.historyLimit, (this.props.config.srcForCustTransactions?this.props.config.srcForCustTransactions:"CH"));

      this.setState({ transactions: orderHistory });

      this._getFilerTreeDataStructure(this.extendedNodes).catch(error => console.log('[EcommSideBarFilter]_getFilerTreeDataStructure error: ', error));
    } 
    else {
      const homeLink = getUrlSync('home', this.props.context.actionContext) || '';

      window.location.assign(homeLink.replace('home', ''));
    }
  }

  public render(): JSX.Element | null {
    const HistoryFilterViewProps = {
      ...this.props,
      filterNodes: this.state.filterTreeNodesState,
      extendedNodes: this.state.extendedNodes,
      totalProducts: this.state.totalProducts
    };

    return this.props.renderView(HistoryFilterViewProps);
  }

  private async _getFilerTreeDataStructure(extended: string[]):Promise<void> {
    const { data: { hierarchy } } = this.props;

    const retObject: ITreeViewNode[] = [];
    const totalProductNumber = this.state.transactions.length;
    
    const promises = hierarchy.result!.sort((a, b) => a.DisplayOrder! - b.DisplayOrder!).map(async elem => {
      if (hierarchy.result![0].ParentCategory! === elem.ParentCategory) {
        await this._addNode(retObject, elem, extended);
      }
    });

    Promise.all(promises).then(() => {
      this.setState({ filterTreeNodesState: retObject, extendedNodes: extended, totalProducts: totalProductNumber });
    })
    .catch(error => {
      console.log('[_getFilerTreeDataStructure] error: ', error);
    });
  }

  private async _addNode(tree: ITreeViewNode[], node: CategoryHierarchy, extended: string[]): Promise<ITreeViewNode[]> {
    if (!node) {
      return [];
    }

    const totalProductNumber = this._getCountByCategory(node.RecordId);

    let newTreeNode: ITreeViewNode = { value: '', label: '', name: '' };
  
    newTreeNode.value = node.RecordId.toString();
    newTreeNode.label = `${node.Name} (${totalProductNumber})`;
    newTreeNode.name = node.Name ? node.Name : '';

    // Add record to extended array.
    extended.push(node.RecordId.toString());

    tree.push(newTreeNode);

    return tree;
  }

  private _getCountByCategory(categoryId: number): number {
    return this.state.transactions.filter(transaction => Number(transaction.Record.ProductCategoryID) == categoryId).length;
  }
}

export default EcommHistoryFilter;
